/***
*
*   SIGN IN
*   Sign in form for all account types (including master).
*
**********/

import React, { useContext, useState, useEffect } from 'react';
import {
    TextField,
    Autocomplete,
    Grid,
    Typography,
    Divider,
    IconButton,
    InputAdornment,
    Avatar,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AuthContext, ViewContext, Link, useLocation, useNavigate } from 'components/lib';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { jwtDecode } from 'jwt-decode';
import Styles from './SigninForm.module.scss';
import codes from 'country-calling-code';
import axios from 'axios';
import { OtpVerification } from './otpVerify';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import CaptchaField from 'components/global/CaptchaField/CaptchaField';

export function Signin(props) {
    // context
    const authContext = useContext(AuthContext);
    const viewContext = useContext(ViewContext);
    const location = useLocation();
    const navigate = useNavigate();

    // state
    const [loading, setLoading] = useState(false);
    const [signInMethod, setSignInMethod] = useState('phone'); // 'phone', 'email', or 'magic'
    const [showPassword, setShowPassword] = useState(false);
    const [magicLinkSent, setMagicLinkSent] = useState(false);
    const [showOtherOptions, setShowOtherOptions] = useState(false);
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [otp, setOtp] = useState('');
    const [returningUser, setReturningUser] = useState(null);
    const [showRegularSignIn, setShowRegularSignIn] = useState(false);

    // form values
    const [values, setValues] = useState({
        countryCode: '91', // Default country code
        phoneNumber: '',
        email: '',
        password: '',
    });
    const [captcha, setCaptcha] = useState({
        image: "",
        id: "",
        input: ""
    })

    // errors and validation
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const phoneRegex = /^\s*(\d\s*){7,15}\s*$/; // Valid phone number
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Valid email
    const captchaRegex = /^-?\d+$/; //Only -ve/+ve integers


    const redirectLink = location?.state?.redirectTo || '/navigate';

    //useEffects
    useEffect(() => {
        if (signInMethod === 'phone' && !showOtpInput) {
            refreshCaptcha();
        }
    }, [signInMethod, showOtpInput])

    useEffect(() => {
        // Check for returning user in localStorage
        const lastUser = localStorage.getItem('lastSignedInUser');
        if (lastUser) {
            const userData = JSON.parse(lastUser);
            setReturningUser(userData);

            // Pre-populate form fields based on last sign-in method
            if (userData.method === 'phone') {
                setSignInMethod('phone');
                setValues(prev => ({
                    ...prev,
                    countryCode: userData.countryCode || '91',
                    phoneNumber: userData.phoneNumber || ''
                }));
            } else if (userData.method === 'email' || userData.method === 'magic') {
                setSignInMethod(userData.method);
                setValues(prev => ({
                    ...prev,
                    email: userData.email || ''
                }));
            }
        }

        // Check for error in URL
        const qs = location.search;
        if (qs.includes('error')) {
            const msg = decodeURIComponent(qs.substring(qs.indexOf('?error=') + 7));
            viewContext.notification.show(msg, 'error');
        }
    }, [location.search, viewContext.notification]);

    useEffect(() => {
        if (authContext.user?.token) {
            try {
                const decodedToken = jwtDecode(authContext.user.token);
                const expirationTime = decodedToken.exp * 1000;
                const currentTime = Date.now();

                if (currentTime < expirationTime) {
                    navigate("/navigate");
                }
            } catch {
                // Token decode failed
            }
        }
    }, [authContext.user?.token, navigate]);

    const refreshCaptcha = async () => {
        try {
            const res = await axios.get("/api/auth/captcha");
            setCaptcha({
                image: res.data.image,
                id: res.data.captchaId,
                input: "",
            });
        } catch (err) {
            viewContext.notification.show("Failed to load captcha. Please try again later", "error", true);
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues({
            ...values,
            [name]: value,
        });
    };

    const handleBlur = (field) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        setErrors(validate());
    };

    const validate = () => {
        const newErrors = {};

        if (signInMethod === 'phone') {
            if (!phoneRegex.test(values.phoneNumber)) {
                newErrors.phoneNumber = 'Enter a valid phone number';
            }
            if (!captchaRegex.test(captcha.input)) {
                newErrors.captcha = 'Enter a valid captcha';
            }
        } else if (signInMethod === 'email') {
            if (!emailRegex.test(values.email)) {
                newErrors.email = 'Enter a valid email address';
            }
            if (!values.password) {
                newErrors.password = 'Password is required';
            }
        } else if (signInMethod === 'magic') {
            if (!emailRegex.test(values.email)) {
                newErrors.email = 'Enter a valid email address';
            }
        }

        return newErrors;
    };

    const handleSubmit = () => {
        const formErrors = validate();
        setErrors(formErrors);

        if (signInMethod === 'phone') {
            setTouched({
                phoneNumber: true,
                captcha: true,
            });
        } else {
            setTouched({
                email: true,
                ...(signInMethod === 'email' ? { password: true } : {}),
            });
        }

        if (Object.keys(formErrors).length === 0) {
            setLoading(true);

            let options = {
                method: 'POST'
            };

            if (signInMethod === 'phone') {
                const queryParams = new URLSearchParams({
                    sign_in: true,
                    mobile: true
                }).toString();

                options.url = `/api/auth/otp?${queryParams}`;
                options.data = {
                    mobile_number: `+${values.countryCode} ${values.phoneNumber}`,
                    captcha_id: captcha.id,
                    captcha_input: captcha.input,
                };
            } else if (signInMethod === 'email') {
                options.url = '/api/auth';
                options.data = {
                    email: values.email,
                    password: values.password
                };
            } else if (signInMethod === 'magic') {
                options.url = '/api/auth/magic';
                options.data = {
                    email: values.email
                };
            }

            axios
                .request(options)
                .then((response) => {
                    response.data.redirectLink = redirectLink;
                    if (signInMethod === 'magic') {
                        setMagicLinkSent(true);
                    } else if (signInMethod === 'phone') {
                        if (response.data.otp_sent.mobile_otp === true) {
                            setShowOtpInput(true);
                            setLoading(false);
                            viewContext.notification.show(response.data.message, 'success', true);
                        } else {
                            viewContext.notification.show(response.data.message, 'error');
                        }
                    } else {
                        if (response.data && response.data['2fa_required']) {
                            navigate(`/signin/otp?token=${response?.data?.token}`);
                        } else {
                            // Save user info to localStorage before signing in
                            saveUserToLocalStorage(response?.data);
                            authContext.signin(response);
                        }
                    }
                })
                .catch((error) => {
                    setLoading(false);

                    const data = error.response?.data;
                    const message = data?.message || data?.error || data;
                    if (["CAPTCHA_EXPIRED", "CAPTCHA_INVALID"].includes(data?.errorCode) && data?.captcha) {
                        setCaptcha({ id: data.captcha.captchaId, image: data.captcha.image, input: "" });
                    }
                    viewContext.notification.show(message, 'error');
                });
        }
    };

    const saveUserToLocalStorage = (user) => {
        // Store last signed in user info
        const userToStore = {
            name: user.first_name || user.email || `+${values.countryCode} ${values.phoneNumber}`,
            email: user.email || '',
            method: signInMethod,
            countryCode: values.countryCode,
            phoneNumber: values.phoneNumber,
            avatar: user.avatar || null
        };
        localStorage.setItem('lastSignedInUser', JSON.stringify(userToStore));
    };

    const handleResendOtp = () => {
        const queryParams = new URLSearchParams({
            mobile: true
        }).toString();

        const options = {
            method: 'POST',
            url: `/api/auth/otp/resend?${queryParams}`,
            data: {
                mobile_number: `+${values.countryCode} ${values.phoneNumber}`,
            },
        };

        axios
            .request(options)
            .then((response) => {
                if ((response.data.otp_sent.email_otp === true) || (response.data.otp_sent.mobile_otp === true)) {
                    viewContext.notification.show(response.data.message, 'success', true);
                } else {
                    viewContext.notification.show(response.data.message, 'error');
                }
            })
            .catch((error) => {
                const data = error.response?.data;
                const message = data?.message || data?.error || data;
                if (["CAPTCHA_EXPIRED", "CAPTCHA_INVALID"].includes(data?.errorCode) && data?.captcha) {
                    setCaptcha({ id: data.captcha.captchaId, image: data.captcha.image, input: "" });
                }
                if (showOtpInput) {
                    setShowOtpInput(false);
                    setOtp('')
                }
                viewContext.notification.show(message, 'error');
            })
    };

    const handleOtpVerify = (otp) => {
        if (loading) return;
        setLoading(true);

        const options = {
            method: 'POST',
            url: '/api/auth/mobile/verify',
            data: {
                country_code: values.countryCode,
                phone_number: values.phoneNumber,
                otp: otp
            }
        }
        axios
            .request(options)
            .then(function(response) {
                if (response.data) {
                    response.data.redirectLink = redirectLink;
                    if (response.data['2fa_required']) {
                        navigate(`/signin/otp?token=${response.data.token}`);
                    } else {
                        // Save user info to localStorage before signing in
                        saveUserToLocalStorage(response.data);
                        authContext.signin(response);
                    }
                } else {
                    setLoading(false);
                    viewContext.notification.show(response.data.message, 'error');
                }
            })
            .catch((error) => {
                setLoading(false);
                const data = error.response?.data;
                const message = data?.message || data?.error || data;
                if (["CAPTCHA_EXPIRED", "CAPTCHA_INVALID"].includes(data?.errorCode) && data?.captcha) {
                    setCaptcha({ id: data.captcha.captchaId, image: data.captcha.image, input: "" });
                }
                if (showOtpInput) {
                    setShowOtpInput(false);
                    setOtp('')
                }
                viewContext.notification.show(message, 'error');
            })
    }

    const handleSignInAsDifferentUser = () => {
        localStorage.clear();
        setReturningUser(null);
        setShowRegularSignIn(true);
        setValues({
            countryCode: '91',
            phoneNumber: '',
            email: '',
            password: '',
        });
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const switchToPhoneSignIn = () => {
        setSignInMethod('phone');
        setTouched({});
        setErrors({});
        setShowOtpInput(false);
    };

    const switchToEmailSignIn = () => {
        setSignInMethod('email');
        setTouched({});
        setErrors({});
    };

    const switchToMagicSignIn = () => {
        setSignInMethod('magic');
        setTouched({});
        setErrors({});
    };

    const toggleOtherOptions = () => {
        setShowOtherOptions(!showOtherOptions);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmit();
        }
    };

    const handleCaptchaInput = (input) => {
        setCaptcha((prev) => ({ ...prev, input: input }));
    }


    if (magicLinkSent) {
        return (
            <div className={Styles.formContainer}>
                <div className={Styles.successMessageContainer}>
                    <Typography
                        variant="h5"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '16px',
                            fontWeight: 600,
                            color: '#1450a0'
                        }}
                    >
                        <EmailIcon sx={{ marginRight: '8px' }} />
                        Magic Link Sent
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            marginBottom: '16px',
                            color: 'text.secondary',
                            textAlign: 'center'
                        }}
                    >
                        We've sent a magic link to your email address. Check your inbox and click the link to sign in.
                    </Typography>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <LoadingBackdrop isOpen={true} />
        )
    }

    // Returning user view (Welcome back flow)
    if (returningUser && !showRegularSignIn && !loading && !showOtpInput) {
        return (
            <div className={Styles.formContainer}>
                <div className={Styles.welcomeBackContainer}>
                    <Avatar
                        sx={{
                            width: 60,
                            height: 60,
                            marginBottom: '16px',
                            bgcolor: '#1450a0'
                        }}
                        src={returningUser.avatar || undefined}
                    >
                        {!returningUser.avatar && <PersonIcon sx={{ fontSize: 30 }} />}
                    </Avatar>

                    <Typography
                        variant="h5"
                        sx={{
                            marginBottom: '8px',
                            fontWeight: 600,
                            color: '#1450a0',
                            textAlign: 'center'
                        }}
                    >
                        Welcome back, {returningUser.name}
                    </Typography>

                    {returningUser.method === 'phone' && (
                        <Typography
                            variant="subtitle1"
                            sx={{
                                marginBottom: '20px',
                                color: 'text.secondary',
                                textAlign: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <PhoneIcon sx={{ marginRight: '8px', fontSize: 18 }} />
                            +{returningUser.countryCode} {returningUser.phoneNumber}
                        </Typography>
                    )}

                    {returningUser.method === 'email' && (
                        <>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    marginBottom: '16px',
                                    color: 'text.secondary',
                                    textAlign: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <EmailIcon sx={{ marginRight: '8px', fontSize: 18 }} />
                                {returningUser.email}
                            </Typography>

                            <div className={Styles.formFieldContainer}>
                                <TextField
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('password')}
                                    onKeyDown={handleKeyDown}
                                    error={touched.password && !!errors.password}
                                    helperText={touched.password && errors.password}
                                    required
                                    fullWidth
                                    autoFocus
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>

                            <div className={Styles.forgotPasswordLink}>
                                <Link url="/forgotpassword" text="Forgot Password?" />
                            </div>
                        </>
                    )}

                    {returningUser.method === 'magic' && (
                        <Typography
                            variant="body1"
                            sx={{
                                marginBottom: '20px',
                                color: 'text.secondary',
                                textAlign: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <EmailIcon sx={{ marginRight: '8px', fontSize: 18 }} />
                            {returningUser.email}
                        </Typography>
                    )}

                    <AivagamButton
                        id="signInButton"
                        buttonText={returningUser.method === 'magic' ? "Send Magic Link" : "Sign In"}
                        onClick={handleSubmit}
                        classStyles={{
                            width: "70%",
                            fontSize: "1.1em",
                            padding: "0.6em",
                            marginBottom: "1em",
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        type="confirm"
                        loading={loading}
                    />

                    <div className={Styles.differentUserLink}>
                        <Typography
                            onClick={handleSignInAsDifferentUser}
                            sx={{
                                cursor: 'pointer',
                                color: '#1450a0',
                                textDecoration: 'underline',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                textAlign: 'center',
                                marginTop: '16px'
                            }}
                        >
                            Sign-in as a different user or use another sign-in method
                        </Typography>
                    </div>
                </div>
            </div>
        );
    } else if (showOtpInput && returningUser && !loading) {
        return (
            <div className={Styles.formContainer}>
                <OtpVerification
                    OTP={otp}
                    setOTP={setOtp}
                    onEdit={() => { setShowOtpInput(false); setLoading(false); }}
                    onVerify={handleOtpVerify}
                    onResend={handleResendOtp}
                    onSubmitNewOtp={handleSubmit}
                    signUpMethod={'phone'}
                    destination={values.phoneNumber}
                    t={props.t}
                    loading={loading}
                />
            </div>
        )
    }

    // Regular sign-in flow
    return (
        <div className={Styles.formContainer}>
            {signInMethod === 'phone' && !showOtpInput && (
                <>
                    <Typography
                        variant="h5"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '24px',
                            fontWeight: 600,
                            color: '#1450a0'
                        }}
                    >
                        <PhoneIcon sx={{ marginRight: '8px' }} />
                        Sign in with Mobile
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            marginTop: '4px',
                            marginBottom: '16px',
                            color: 'text.secondary',
                            textAlign: 'center'
                        }}
                    >
                        Enter your phone number to sign in to your account.
                    </Typography>

                    <div className={Styles.formFieldContainer}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Autocomplete
                                    options={codes}
                                    getOptionLabel={(option) => `${option.isoCode3} +${option.countryCodes[0]}`}
                                    value={codes.find(option => option.countryCodes[0] === values.countryCode) || null}
                                    onChange={(event, newValue) => {
                                        handleChange({ target: { name: 'countryCode', value: newValue?.countryCodes[0] || '' } });
                                    }}
                                    disableClearable
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Country"
                                            name="countryCode"
                                            required
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <TextField
                                    label="Phone Number"
                                    name="phoneNumber"
                                    type="tel"
                                    value={values.phoneNumber}
                                    onChange={handleChange}
                                    placeholder="1234567890"
                                    onBlur={() => handleBlur('phoneNumber')}
                                    onKeyDown={handleKeyDown}
                                    error={touched.phoneNumber && !!errors.phoneNumber}
                                    helperText={touched.phoneNumber && errors.phoneNumber}
                                    required
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </div>
                    <div className={Styles.formFieldContainer}>
                        {!!captcha?.image && (
                            <CaptchaField
                                captcha={captcha}
                                onRefreshCaptcha={refreshCaptcha}
                                onCaptchaChange={handleCaptchaInput}
                                onBlur={handleBlur}
                                error={touched.captcha && !!errors.captcha}
                                helperText={touched.captcha && errors.captcha}
                            />
                        )}
                    </div>

                    <AivagamButton
                        id="signInButton"
                        buttonText="Sign In"
                        onClick={handleSubmit}
                        classStyles={{
                            width: "50%",
                            fontSize: "1em",
                            padding: "0.5em",
                            marginBottom: "0.6em",
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        type="confirm"
                        loading={loading}
                    />
                </>
            )}

            {signInMethod === 'phone' && showOtpInput && (
                <OtpVerification
                    OTP={otp}
                    setOTP={setOtp}
                    onEdit={() => { setShowOtpInput(false); setLoading(false); }}
                    onVerify={handleOtpVerify}
                    onResend={handleResendOtp}
                    onSubmitNewOtp={handleSubmit}
                    signUpMethod={'phone'}
                    destination={values.phoneNumber}
                    t={props.t}
                    loading={loading}
                />
            )}

            {signInMethod === 'email' && (
                <>
                    <Typography
                        variant="h5"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '24px',
                            fontWeight: 600,
                            color: '#1450a0'
                        }}
                    >
                        <EmailIcon sx={{ marginRight: '8px' }} />
                        Sign in with Email
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            marginTop: '4px',
                            marginBottom: '16px',
                            color: 'text.secondary',
                            textAlign: 'center'
                        }}
                    >
                        Enter your email and password to sign in.
                    </Typography>

                    <div className={Styles.formFieldContainer}>
                        <TextField
                            label="Email"
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={() => handleBlur('email')}
                            onKeyDown={handleKeyDown}
                            error={touched.email && !!errors.email}
                            helperText={touched.email && errors.email}
                            required
                            fullWidth
                        />
                    </div>

                    <div className={Styles.formFieldContainer}>
                        <TextField
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={() => handleBlur('password')}
                            onKeyDown={handleKeyDown}
                            error={touched.password && !!errors.password}
                            helperText={touched.password && errors.password}
                            required
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>

                    <div className={Styles.forgotPasswordLink}>
                        <Link url="/forgotpassword" text="Forgot Password?" />
                    </div>

                    <AivagamButton
                        id="signInEmailButton"
                        buttonText="Sign In"
                        onClick={handleSubmit}
                        classStyles={{
                            width: "50%",
                            fontSize: "1em",
                            padding: "0.5em",
                            marginBottom: "0.6em",
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        type="confirm"
                        loading={loading}
                    />
                </>
            )}

            {signInMethod === 'magic' && (
                <>
                    <Typography
                        variant="h5"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '24px',
                            fontWeight: 600,
                            color: '#1450a0'
                        }}
                    >
                        <LinkIcon sx={{ marginRight: '8px' }} />
                        Sign in with Magic Link
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            marginTop: '4px',
                            marginBottom: '16px',
                            color: 'text.secondary',
                            textAlign: 'center'
                        }}
                    >
                        We'll send you an email with a magic link to sign in instantly.
                    </Typography>

                    <div className={Styles.formFieldContainer}>
                        <TextField
                            label="Email"
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={() => handleBlur('email')}
                            onKeyDown={handleKeyDown}
                            error={touched.email && !!errors.email}
                            helperText={touched.email && errors.email}
                            required
                            fullWidth
                        />
                    </div>

                    <AivagamButton
                        id="magicLinkButton"
                        buttonText="Send Magic Link"
                        onClick={handleSubmit}
                        classStyles={{
                            width: "50%",
                            fontSize: "1em",
                            padding: "0.5em",
                            marginBottom: "0.6em",
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        type="confirm"
                        loading={loading}
                    />
                </>
            )}

            {!showOtherOptions ? (
                <div className={Styles.otherOptionsToggle}>
                    <Typography
                        onClick={toggleOtherOptions}
                        sx={{
                            cursor: 'pointer',
                            color: '#1450a0',
                            textDecoration: 'underline',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        Other Sign In Options
                        <KeyboardArrowDownIcon fontSize="small" />
                    </Typography>
                </div>
            ) : (
                <>
                    <div className={Styles.dividerContainer}>
                        <Divider className={Styles.dividerLine} />
                        <Typography className={Styles.dividerText}>Other Sign In Options</Typography>
                        <Divider className={Styles.dividerLine} />
                    </div>

                    {signInMethod !== 'phone' && (
                        <AivagamButton
                            id="phoneSignInButton"
                            buttonText="Sign in with Phone Number"
                            onClick={switchToPhoneSignIn}
                            disabled={true}
                            classStyles={{
                                width: "50%",
                                fontSize: "1em",
                                padding: "0.5em",
                                marginBottom: "0.6em",
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        />
                    )}

                    {signInMethod !== 'email' && (
                        <AivagamButton
                            id="emailSignInButton"
                            buttonText="Sign in with Email & Password"
                            onClick={switchToEmailSignIn}
                            classStyles={{
                                width: "50%",
                                fontSize: "1em",
                                padding: "0.5em",
                                marginBottom: "0.6em",
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        />
                    )}

                    {signInMethod !== 'magic' && (
                        <AivagamButton
                            id="magicLinkSignInButton"
                            buttonText="Sign in with Magic Link"
                            onClick={switchToMagicSignIn}
                            classStyles={{
                                width: "50%",
                                fontSize: "1em",
                                padding: "0.5em",
                                marginBottom: "0.6em",
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        />
                    )}

                    <div className={Styles.hideOptionsButton}>
                        <Typography
                            onClick={toggleOtherOptions}
                            sx={{
                                cursor: 'pointer',
                                color: '#1450a0',
                                textDecoration: 'underline',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                textAlign: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginTop: '8px'
                            }}
                        >
                            Hide Options
                            <KeyboardArrowUpIcon fontSize="small" />
                        </Typography>
                    </div>
                </>
            )}

            <div className={Styles.signUpLink}>
                Don't have an account?
                <p
                    onClick={() => {
                        navigate('/signup', {
                            state: { redirectTo: redirectLink }
                        })
                    }}
                    style={{ color: 'blue', cursor: 'pointer', display: 'inline', textDecoration: 'underline' }}>
                    Sign Up
                </p>
            </div>
        </div>
    )
}
